import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
    Container, Box, Avatar, Typography, TextField, Button, CircularProgress, InputAdornment, Divider,
} from '@mui/material';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import { AppDispatch } from '../../../redux/store';
import { ValidateCompanyCodeService } from '../../../utils/services/auth.service';
import { showSnackbar } from '../../../redux/reducer/snackbarSlice';
import { NAVIGATE_AUTH } from '../../../constant';

const CompanySelection = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch<AppDispatch>();
    const [companyCode, setCompanyCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [formHeight, setFormHeight] = useState(0);
    const formRef = useRef<HTMLDivElement>(null);

    const updateHeight = useCallback(() => {
        const height = formRef.current?.getBoundingClientRect().height || 0;
        setFormHeight(height);
    }, []);

    useEffect(() => {
        updateHeight();

        const observer = new ResizeObserver(() => {
            updateHeight();
        });

        if (formRef.current) observer.observe(formRef.current);

        const intervalId = setInterval(updateHeight, 100);

        return () => {
            observer.disconnect();
            clearInterval(intervalId);
        };
    }, [updateHeight]);

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = e.target.value.toUpperCase().trim();
        const cleanedValue = rawValue.replace(/[^A-Z0-9]/g, '').slice(0, 6);
        setCompanyCode(cleanedValue);
        setError('');
    };


    const handleSubmit = async () => {
        if (!companyCode) {
            setError('Please enter a company code.');
            dispatch(showSnackbar({ type: 'error', message: 'Please enter a company code.' }));
            return;
        }

        if (!/^[A-Z0-9]{1,6}$/.test(companyCode)) {
            setError('Company code must be uppercase, alphanumeric, and up to 6 characters.');
            dispatch(showSnackbar({ type: 'error', message: 'Invalid company code format.' }));
            return;
        }

        setLoading(true);
        const { code, data, message } = await ValidateCompanyCodeService({ company_code: companyCode });
        setLoading(false);

        if (code === 200 && data?.data?.company_name) {
            localStorage.setItem('current_company_code', companyCode);
            // localStorage.setItem('current_company_id', encryptText(response.data.companyUUID));
            const query = new URLSearchParams(location.search).toString();
            navigate(`/${companyCode}${NAVIGATE_AUTH.LOGIN_PAGE}${query ? `?${query}` : ''}`, {
                state: {
                    companyName: data?.data?.company_name,
                    companyCode,
                },
            });

        } else {
            setError('Invalid company code.');
            dispatch(showSnackbar({ type: 'error', message: 'Invalid company code. Please try again.' }));
        }
    };

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundImage: 'url(/images/login_background.png)',
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                zIndex: -1,
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(15, 24, 80, 0.34)',
                    backdropFilter: 'blur(4px)', 
                    WebkitBackdropFilter: 'blur(4px)',
                    zIndex: -1,
                },
            }}
        >
            <Container
                component="main"
                maxWidth="xs"
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        p: 3,
                        borderRadius: 3,
                        bgcolor: '#fff',
                        boxShadow: '0px 8px 16px rgba(0,0,0,0.1)',
                        my: 2,
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 64, height: 64, mb: 2, boxShadow: '0px 4px 10px rgba(0,0,0,0.1)' }}>
                            <BusinessOutlinedIcon fontSize="large" />
                        </Avatar>
                        <Typography component="h1" variant="h5" fontWeight={700} textAlign="center" color="text.primary">
                            Select Company
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2, textAlign: 'center', fontWeight: 500 }}>
                            Enter your company code to proceed to login.
                        </Typography>
                    </Box>
                    <Box
                        ref={formRef}
                        sx={{
                            position: 'relative',
                            width: '100%',
                            minHeight: formHeight,
                            height: 'auto',
                            overflow: 'hidden',
                            transition: 'min-height 0.3s ease',
                        }}
                    >
                        <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} noValidate sx={{ width: '100%' }}>
                            <TextField
                                variant="outlined"
                                size="small"
                                margin="normal"
                                required
                                fullWidth
                                placeholder="Enter company code"
                                label="Company Code"
                                value={companyCode}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOnChange(e)}
                                error={!!error}
                                helperText={error}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="end">
                                            <BusinessOutlinedIcon fontSize="small" sx={{ mr: 1 }} color="primary" />
                                            <Divider orientation="vertical" sx={{ height: 15 }} />
                                        </InputAdornment>
                                    ),
                                }}
                                inputProps={{
                                    maxLength: 6, 
                                    pattern: "[A-Z0-9]*",
                                }}
                                sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                            {error && (
                                <Typography variant="body2" color="error" textAlign="center" sx={{ mb: 1 }}>
                                    {error}
                                </Typography>
                            )}
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                color="primary"
                                sx={{ mt: 2, py: 1.2, fontWeight: 600, borderRadius: 2, boxShadow: '0px 4px 12px rgba(33, 150, 243, 0.3)' }}
                                disabled={loading || !companyCode}
                                aria-label="Continue"
                            >
                                {loading ? (
                                    <>
                                        Validating...
                                        <CircularProgress size={20} sx={{ color: 'primary.light', ml: 1 }} />
                                    </>
                                ) : (
                                    'Continue'
                                )}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
};

export default CompanySelection;